import { useCallback, useState } from "react";

// Shared busy/error/notice trio for one-shot async actions (publish, queue,
// connect, save). Replaces the copy-pasted useState triples across the
// publish buttons, schedule controls, and connect panels.

export type AsyncAction = {
  busy: boolean;
  error: string;
  notice: string;
  /** Runs the action; on throw, sets error (falling back to fallbackError). */
  run: (
    action: () => Promise<string | void>,
    opts?: { fallbackError?: string }
  ) => Promise<void>;
  reset: () => void;
  setError: (message: string) => void;
  setNotice: (message: string) => void;
};

export const useAsyncAction = (): AsyncAction => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const run = useCallback(
    async (
      action: () => Promise<string | void>,
      opts?: { fallbackError?: string }
    ) => {
      setBusy(true);
      setError("");
      setNotice("");
      try {
        const result = await action();
        if (typeof result === "string" && result) setNotice(result);
      } catch (e: any) {
        setError(e?.message || opts?.fallbackError || "Something went wrong.");
      } finally {
        setBusy(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setError("");
    setNotice("");
  }, []);

  return { busy, error, notice, run, reset, setError, setNotice };
};

export default useAsyncAction;
